import React, { useRef, useState, useEffect } from 'react';
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  RotateCcw,
  RotateCw,
  SkipBack,
  SkipForward,
  X,
  Mic,
  Calendar,
} from 'lucide-react';

export default function JubileeAudioPlayer({ currentTrack, isPlaying, onTogglePlay, onNextTrack, onPrevTrack, onClose }) {
  const audioRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);

  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.play().catch((e) => console.log('Audio play error:', e));
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying, currentTrack]);

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      setDuration(audioRef.current.duration || 0);
    }
  };

  const handleSeek = (e) => {
    const time = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const handleVolumeChange = (e) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    if (audioRef.current) {
      audioRef.current.volume = val;
      setIsMuted(val === 0);
    }
  };

  const toggleMute = () => {
    if (!audioRef.current) return;
    if (isMuted) {
      audioRef.current.muted = false;
      setIsMuted(false);
      audioRef.current.volume = volume || 0.6;
    } else {
      audioRef.current.muted = true;
      setIsMuted(true);
    }
  };

  const skipForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(audioRef.current.duration || 0, audioRef.current.currentTime + 10);
    }
  };

  const skipBackward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 10);
    }
  };

  const handleSpeedChange = (speed) => {
    setPlaybackSpeed(speed);
    if (audioRef.current) {
      audioRef.current.playbackRate = speed;
    }
  };

  const formatTime = (secs) => {
    if (isNaN(secs)) return '00:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m < 10 ? '0' : ''}${m}:${s < 10 ? '0' : ''}${s}`;
  };

  if (!currentTrack) return null;

  return (
    <div
      className="fixed-bottom shadow-2xl"
      style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        color: '#ffffff',
        borderTop: '2px solid #d4af37',
        zIndex: 1050,
        padding: '0.75rem 1.25rem',
      }}
    >
      <audio
        ref={audioRef}
        src={currentTrack.fileUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleTimeUpdate}
        onEnded={onNextTrack || onTogglePlay}
      />

      <div className="container-fluid container-xl">
        <div className="row align-items-center g-3">
          {/* Track Info (Left) */}
          <div className="col-12 col-md-4 d-flex align-items-center gap-3">
            <div className="position-relative" style={{ flexShrink: 0 }}>
              <img
                src={currentTrack.thumbnailUrl || 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&w=200&q=80'}
                alt={currentTrack.title}
                className="rounded-3 shadow object-fit-cover"
                style={{ width: '56px', height: '56px', border: '1px solid rgba(212, 175, 55, 0.4)' }}
              />
              {isPlaying && (
                <div className="position-absolute bottom-0 start-0 end-0 d-flex justify-content-center pb-1">
                  <div className="audio-waveform-bar" style={{ height: '14px' }}>
                    <span className="audio-wave-stick active" style={{ animationDelay: '0.1s' }} />
                    <span className="audio-wave-stick active" style={{ animationDelay: '0.3s' }} />
                    <span className="audio-wave-stick active" style={{ animationDelay: '0.2s' }} />
                  </div>
                </div>
              )}
            </div>

            <div className="overflow-hidden">
              <div className="fw-bold text-white text-truncate small" style={{ letterSpacing: '0.3px' }}>
                {currentTrack.title}
              </div>
              <div className="text-white-50 d-flex align-items-center gap-2" style={{ fontSize: '0.78rem' }}>
                {currentTrack.speaker && (
                  <span className="text-truncate">
                    <Mic size={12} className="text-warning me-1" />
                    {currentTrack.speaker}
                  </span>
                )}
                {currentTrack.year && (
                  <span>
                    <Calendar size={12} className="text-info me-1" />
                    {currentTrack.year}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Player Controls & Progress (Center) */}
          <div className="col-12 col-md-5">
            <div className="d-flex flex-column align-items-center">
              {/* Button Bar */}
              <div className="d-flex align-items-center gap-3 mb-1">
                {onPrevTrack && (
                  <button className="btn btn-link text-white-50 hover-text-white p-0" onClick={onPrevTrack} title="Previous">
                    <SkipBack size={18} />
                  </button>
                )}

                <button className="btn btn-link text-white-50 hover-text-white p-0" onClick={skipBackward} title="Rewind 10s">
                  <RotateCcw size={16} />
                </button>

                <button
                  className="btn rounded-circle d-flex align-items-center justify-content-center shadow"
                  style={{
                    width: '42px',
                    height: '42px',
                    background: 'linear-gradient(135deg, #d4af37, #b8860b)',
                    color: '#fff',
                    border: 'none',
                  }}
                  onClick={onTogglePlay}
                  title={isPlaying ? 'Pause' : 'Play'}
                >
                  {isPlaying ? <Pause size={20} /> : <Play size={20} style={{ marginLeft: '2px' }} />}
                </button>

                <button className="btn btn-link text-white-50 hover-text-white p-0" onClick={skipForward} title="Forward 10s">
                  <RotateCw size={16} />
                </button>

                {onNextTrack && (
                  <button className="btn btn-link text-white-50 hover-text-white p-0" onClick={onNextTrack} title="Next">
                    <SkipForward size={18} />
                  </button>
                )}
              </div>

              {/* Progress Slider */}
              <div className="w-100 d-flex align-items-center gap-2">
                <span className="text-white-50" style={{ fontSize: '0.72rem', fontFamily: 'monospace', minWidth: '35px' }}>
                  {formatTime(currentTime)}
                </span>
                <input
                  type="range"
                  min="0"
                  max={duration || 100}
                  step="0.1"
                  value={currentTime}
                  onChange={handleSeek}
                  className="form-range flex-grow-1"
                  style={{ accentColor: '#d4af37', height: '4px' }}
                />
                <span className="text-white-50" style={{ fontSize: '0.72rem', fontFamily: 'monospace', minWidth: '35px' }}>
                  {formatTime(duration)}
                </span>
              </div>
            </div>
          </div>

          {/* Volume, Speed, Close (Right) */}
          <div className="col-12 col-md-3 d-flex align-items-center justify-content-end gap-3">
            {/* Speed selection */}
            <div className="btn-group btn-group-sm">
              {[0.75, 1, 1.25, 1.5].map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`btn btn-sm py-0 px-1 text-xs ${playbackSpeed === s ? 'btn-warning fw-bold' : 'btn-outline-secondary text-white-50'}`}
                  style={{ fontSize: '0.7rem' }}
                  onClick={() => handleSpeedChange(s)}
                >
                  {s}x
                </button>
              ))}
            </div>

            {/* Volume */}
            <div className="d-flex align-items-center gap-1">
              <button className="btn btn-link text-white-50 p-0" onClick={toggleMute}>
                {isMuted || volume === 0 ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                className="form-range"
                style={{ width: '60px', accentColor: '#d4af37', height: '4px' }}
              />
            </div>

            {/* Close button */}
            {onClose && (
              <button className="btn btn-link text-white-50 hover-text-white p-0 ms-1" onClick={onClose} title="Close Player">
                <X size={20} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
